import React, { useEffect, useState } from "react";
import { Bookmark } from "lucide-react";
import api from "../../lib/apiClient";

// MostSavedRail — anonymous "Most-Saved Creators This Week" panel for
// the public landing. Backed by `GET /api/x/most-saved` (housed in
// the x_signin router because it lives next door to the other
// landing-funnel public endpoints, not because it's X-specific).
//
// Display contract is the same as FoundersLeaderboard: anonymised
// first-name + last-initial only — saves are a viewer-tracked metric,
// so leaking creator identity here would turn a feature into a doxx
// vector. Profiles are still discoverable organically via the feed.
//
// Self-hides on empty — fresh pods and quiet weeks shouldn't show an
// awkward "Top 0" card.
const MostSavedRail = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/x/most-saved?limit=10")
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { /* public endpoint; transient errors are non-actionable */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section
      className="w-full mx-auto max-w-2xl px-4 sm:px-6 my-10 sm:my-14"
      data-testid="most-saved-rail"
    >
      <header className="flex items-center gap-2 mb-4">
        <Bookmark className="h-5 w-5 text-amber-400" />
        <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight">
          Most-Saved Creators
        </h2>
        <span className="text-xs text-gray-500 ml-2 hidden sm:inline">
          videos viewers come back to
        </span>
      </header>
      <ol className="rounded-xl border border-gray-800 bg-gray-950/60 backdrop-blur divide-y divide-gray-800/70 overflow-hidden">
        {items.map((it, idx) => {
          const display = it.last_initial
            ? `${it.first_name} ${it.last_initial}.`
            : it.first_name;
          return (
            <li
              key={`${it.id}-${idx}`}
              className="flex items-center gap-3 px-4 py-3"
              data-testid={`most-saved-row-${idx}`}
            >
              <span className="w-5 text-center text-xs font-mono text-gray-500 shrink-0">
                {idx + 1}
              </span>
              <Bookmark className="h-3.5 w-3.5 text-emerald-400/60 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-200">
                {display}
              </span>
              <span className="text-sm font-semibold tabular-nums text-amber-300">
                {it.save_count}
                <span className="text-[10px] text-gray-500 ml-1 font-normal">saves</span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default MostSavedRail;
